import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit2, Trash2, Plus, X, Search, AlertTriangle, Copy, Maximize2, Settings2, FlaskConical, Bot } from 'lucide-react';
import { LoadingSpinner, MultiSelect } from '../../../../common';
import { AdminCard, AdminEmptyState } from '../../../ui';
import * as dataSourceService from '../../../../../services/datasourceService';
import * as agentService from '../../../../../services/agentService';
import * as workspaceService from '../../../../../services/workspaceService';
import { Agent } from '../../../../../types/agent';
import { PaginationMetadata } from '../../../../../types/user';
import { WorkspaceGroup } from '../../../../../types/workspace';
import { TerminologyParameter, DataSourceWithAgents as DataSource, DataSourceUpdate, RegexTestResult } from '../../../../../types/dataSource';
import { getModelSelectOptions } from '../../../../../config/models';
import { useAuth } from '../../../../../contexts/AuthContext';

interface DataSourceSettingsProps {
  id?: string;
}

export const DataSourceSettings: React.FC<DataSourceSettingsProps> = ({ id }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [groups, setGroups] = useState<(WorkspaceGroup & { workspace_name?: string })[]>([]);
  const [workspaces, setWorkspaces] = useState<{id: number; name: string}[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginationMetadata | null>(null);
  const [formData, setFormData] = useState<DataSourceUpdate>({
    name: '',
    description: '',
    rag_type: 'relational',
    rag_config: {},
    visibility_level: 'workspace',
    created_by_group_id: null,
    workspace_ids: [],
    group_ids: []
  });
  const [expandedPrompt, setExpandedPrompt] = useState<null | { type: 'profile' | 'filter', value: string, editable: boolean }>(null);
  const [regexTestResults, setRegexTestResults] = useState<{ [key: string]: RegexTestResult } | null>(null);
  const [isTestingRegex, setIsTestingRegex] = useState<{[key: string]: boolean}>({});

  const pageLimit = 10;

  const inputBaseClass = `
    w-full px-4 py-3
    bg-[#1a1a1a] border border-[#333333]
    text-white placeholder-[#888888]
    rounded-lg
    focus:outline-none focus:border-[#F4CC04]
    disabled:opacity-60 disabled:cursor-not-allowed
  `;

  const testRegexForParameter = async (parameterKey: string) => {
    try {
      setIsTestingRegex(prev => ({ ...prev, [parameterKey]: true }));
      setError(null);

      const terminologyMapping = formData.rag_config?.profile_extraction?.terminology_mapping || {};
      const paramConfig = terminologyMapping[parameterKey];

      if (!paramConfig || typeof paramConfig !== 'object' || Array.isArray(paramConfig)) {
        throw new Error('Configuração do parâmetro não encontrada');
      }

      const regexConfig = (paramConfig as TerminologyParameter)?.regex_config;

      if (!regexConfig || !regexConfig.enabled) {
        throw new Error('Configuração de regex não habilitada para este parâmetro');
      }

      const testConfig = { [parameterKey]: regexConfig };
      const result = await dataSourceService.testRegexConfiguration(testConfig);

      if (result.success && result.data) {
        setRegexTestResults(prev => ({
          ...(prev || {}),
          [parameterKey]: result.data!
        }));
      }
    } catch (err) {
      setError(`Falha ao testar regex para ${parameterKey}: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsTestingRegex(prev => ({ ...prev, [parameterKey]: false }));
    }
  };

  useEffect(() => {
    const fetchDataSource = async () => {
      if (!id) return;

      try {
        setError(null);
        const response = await dataSourceService.getDataSource(Number(id));
        if (response.success && response.data) {
          setDataSource(response.data);
          setFormData({
            name: response.data.name,
            description: response.data.description || '',
            rag_type: response.data.rag_type || '',
            rag_config: response.data.rag_config || {},
            visibility_level: response.data.visibility_level || 'workspace',
            created_by_group_id: response.data.created_by_group_id || null,
            workspace_ids: response.data.visible_workspace_ids || [],
            group_ids: response.data.visible_group_ids || []
          });

        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Falha ao buscar detalhes da fonte de dados');
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataSource();
  }, [id]);

  // Fetch workspaces on component mount
  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const response = await workspaceService.getUserWorkspaces();
        if (response.success && response.data) {
          setWorkspaces(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch workspaces:', err);
      }
    };
    fetchWorkspaces();
  }, []);

  // Fetch groups from ALL selected workspaces
  useEffect(() => {
    const fetchGroupsFromAllWorkspaces = async () => {
      const selectedWorkspaceIds = formData.workspace_ids;
      if (!selectedWorkspaceIds || selectedWorkspaceIds.length === 0) {
        setGroups([]);
        return;
      }

      try {
        const groupsPromises = selectedWorkspaceIds.map(async (wsId) => {
          const response = await workspaceService.getWorkspaceGroups(wsId);
          if (response.success && response.data) {
            const workspace = workspaces.find(w => w.id === wsId);
            return response.data.map(group => ({
              ...group,
              workspace_name: workspace?.name || `Workspace ${wsId}`
            }));
          }
          return [];
        });

        const allGroupsArrays = await Promise.all(groupsPromises);
        const allGroups = allGroupsArrays.flat();
        setGroups(allGroups);
      } catch (err) {
        console.error('Failed to fetch groups from workspaces:', err);
        setGroups([]);
      }
    };

    fetchGroupsFromAllWorkspaces();
  }, [formData.workspace_ids, workspaces]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAgents = useCallback(async (page: number = 1) => {
    try {
      if (page === 1) {
        setIsLoadingAgents(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);
      setCurrentPage(page);

      const response = await agentService.listAgents(page, pageLimit, debouncedSearchQuery);

      if (response.success && response.data) {
        if (page === 1) {
          setAgents(response.data);
        } else {
          setAgents(prev => [...prev, ...response.data]);
        }
        setPaginationData(response.metadata);
      } else {
        setError(response.message || 'Falha ao carregar agentes');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao buscar agentes');
    } finally {
      setIsLoadingAgents(false);
      setIsLoadingMore(false);
    }
  }, [pageLimit, debouncedSearchQuery]);

  useEffect(() => {
    if (showAgentModal) {
      fetchAgents(1);
    }
  }, [showAgentModal, debouncedSearchQuery, fetchAgents]);

  const handleLoadMore = () => {
    if (paginationData?.has_more && !isLoadingMore) {
      fetchAgents(currentPage + 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const updateData = {
        ...formData,
        workspace_ids: (formData.visibility_level === 'workspace' || formData.visibility_level === 'group') ? formData.workspace_ids : [],
        group_ids: formData.visibility_level === 'group' ? formData.group_ids : []
      };

      const response = await dataSourceService.updateDataSource(Number(id), updateData);

      if (response.success && response.data) {
        const refreshedResponse = await dataSourceService.getDataSource(Number(id));
        if (refreshedResponse.success && refreshedResponse.data) {
          setDataSource(refreshedResponse.data);
        } else {
          setDataSource({ ...response.data, agents: dataSource?.agents ?? [] });
        }
        setIsEditing(false);
      } else {
        setError(response.message || 'Falha ao atualizar fonte de dados');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao atualizar fonte de dados');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssociateAgent = async (agentId: number) => {
    if (!id) return;

    try {
      setError(null);
      const response = await dataSourceService.addAgent(Number(id), agentId);
      if (response.success && response.data) {
        setDataSource(response.data);
        setShowAgentModal(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao associar agente');
    }
  };

  const handleDeassociateAgent = async (agentId: number) => {
    if (!id) return;

    try {
      setError(null);
      const response = await dataSourceService.removeAgent(Number(id), agentId);
      if (response.success && response.data) {
        setDataSource(response.data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao remover associação do agente');
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await dataSourceService.deleteDataSource(Number(id));
      if (response.success) {
        navigate('/admin/datasources');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao excluir fonte de dados');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const filteredAgents = agents.filter(agent =>
    !dataSource?.agents?.some((a: Agent) => a.id === agent.id)
  );

  if (isLoading && !dataSource) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!dataSource) {
    return (
      <AdminEmptyState
        title="Data Source não encontrado"
        description="O data source solicitado não existe ou foi removido"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Settings Card */}
      <AdminCard
        title="Configurações do Data Source"
        headerAction={
          !isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="flex items-center gap-2 px-3 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black text-sm font-medium rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Editar
            </button>
          ) : null
        }
      >
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-white font-medium text-base border-b border-[#333333] pb-2">
              Informações Básicas
            </h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.name || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={!isEditing}
                  required
                  className={inputBaseClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">Descrição</label>
                <textarea
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={!isEditing}
                  rows={3}
                  className={`${inputBaseClass} resize-none`}
                />
              </div>

              {/* Visibility Level Selector */}
              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">
                  Nível de Visibilidade
                </label>
                <select
                  value={formData.visibility_level || 'workspace'}
                  onChange={(e) => {
                    const newLevel = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      visibility_level: newLevel,
                      workspace_ids: (newLevel === 'workspace' || newLevel === 'group') ? prev.workspace_ids : [],
                      group_ids: newLevel === 'group' ? prev.group_ids : []
                    }));
                  }}
                  disabled={!isEditing}
                  className={`${inputBaseClass} cursor-pointer`}
                >
                  <option value="workspace">Workspace (Visível em workspaces selecionados)</option>
                  <option value="group">Grupo (Compartilhamento granular por workspaces e grupos)</option>
                  {user?.isSuperAdmin && (
                    <option value="global">Global (Visível em todos os workspaces)</option>
                  )}
                </select>
                <p className="mt-1 text-xs text-[#888888]">
                  {formData.visibility_level === 'global' && 'Esta fonte de dados será visível em todos os workspaces do sistema'}
                  {formData.visibility_level === 'workspace' && 'Selecione os workspaces onde esta fonte de dados estará disponível'}
                  {formData.visibility_level === 'group' && 'Selecione primeiro os workspaces, depois os grupos específicos que poderão ver esta fonte de dados'}
                </p>
              </div>

              {/* Workspace Multi-Selector (if visibility_level === 'workspace' OR 'group') */}
              {(formData.visibility_level === 'workspace' || formData.visibility_level === 'group') && (
                <MultiSelect
                  label="Workspaces"
                  options={workspaces.map(w => ({ id: w.id, name: w.name }))}
                  selectedIds={formData.workspace_ids || []}
                  onChange={(ids) => setFormData(prev => ({ ...prev, workspace_ids: ids }))}
                  placeholder="Selecione os workspaces..."
                  disabled={!isEditing}
                  required
                />
              )}

              {/* Group Multi-Selector (if visibility_level === 'group') */}
              {formData.visibility_level === 'group' && (
                <MultiSelect
                  label="Grupos"
                  options={groups
                    .filter((g): g is WorkspaceGroup & { workspace_name?: string } => !g.deleted)
                    .map(g => ({
                      id: g.id,
                      name: g.workspace_name ? `${g.name} - ${g.workspace_name}` : g.name
                    }))
                  }
                  selectedIds={formData.group_ids || []}
                  onChange={(ids) => setFormData(prev => ({ ...prev, group_ids: ids }))}
                  placeholder="Selecione os grupos..."
                  disabled={!isEditing}
                  required
                />
              )}

              <div>
                <label className="block text-sm font-medium text-[#cccccc] mb-2">Tipo RAG</label>
                <select
                  value={formData.rag_type || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, rag_type: e.target.value }))}
                  disabled={!isEditing}
                  className={`${inputBaseClass} cursor-pointer`}
                >
                  <option value="relational">Relational</option>
                </select>
              </div>
            </div>
          </div>

          {/* RAG Configuration */}
          {formData.rag_type === 'relational' && (
            <div className="space-y-6">
              {/* Profile Extraction */}
              <div className="p-4 bg-[#1a1a1a] rounded-xl border border-[#333333]">
                <h4 className="text-white font-medium mb-4 pb-2 border-b border-[#333333]">
                  1. Extração de Perfil
                </h4>

                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-[#cccccc]">System Prompt</label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setExpandedPrompt({
                            type: 'profile',
                            value: formData.rag_config?.profile_extraction?.system_prompt || '',
                            editable: isEditing
                          })}
                          className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded transition-colors"
                          title="Expandir"
                        >
                          <Maximize2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopy(formData.rag_config?.profile_extraction?.system_prompt || '')}
                          className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded transition-colors"
                          title="Copiar"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={formData.rag_config?.profile_extraction?.system_prompt || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          profile_extraction: {
                            ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                            system_prompt: e.target.value
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      rows={8}
                      className={`${inputBaseClass} font-mono text-sm resize-y min-h-[180px]`}
                      placeholder="System prompt para o LLM auxiliar que extrai informações de perfil"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">Modelo</label>
                    <select
                      value={formData.rag_config?.profile_extraction?.model_name || 'claude-4-5-haiku'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          profile_extraction: {
                            ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                            model_name: e.target.value
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      className={`${inputBaseClass} cursor-pointer`}
                    >
                      {getModelSelectOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}{option.isRecommended && ' ⭐'}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Terminology Mapping */}
                  <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">
                      Mapeamento de Terminologia
                    </label>
                    <div className="space-y-3 max-h-[400px] overflow-y-auto p-2 bg-[#0a0a0a] rounded-lg border border-[#333333]">
                      {Object.entries(formData.rag_config?.profile_extraction?.terminology_mapping || {}).map(([key, paramConfig]) => {
                        const values = Array.isArray(paramConfig) ? paramConfig : ((paramConfig as TerminologyParameter).values || []);
                        const regexConfig = Array.isArray(paramConfig) ? {
                          enabled: false,
                          include_pattern: null,
                          exclude_pattern: null,
                          case_insensitive: true,
                          max_values: -1
                        } : ((paramConfig as TerminologyParameter).regex_config || {
                          enabled: false,
                          include_pattern: null,
                          exclude_pattern: null,
                          case_insensitive: true,
                          max_values: -1
                        });

                        return (
                          <div key={key} className="p-3 bg-[#1a1a1a] rounded-lg border border-[#333333]">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white font-medium text-sm">{key}</span>
                              {isEditing && (
                                <div className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newRegexConfig = {
                                        ...regexConfig,
                                        enabled: !regexConfig.enabled,
                                        include_pattern: !regexConfig.enabled && !regexConfig.include_pattern ? ".*" : regexConfig.include_pattern
                                      };
                                      const newParamConfig = { values, regex_config: newRegexConfig };
                                      const newMapping = {
                                        ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                        [key]: newParamConfig
                                      };
                                      setFormData(prev => ({
                                        ...prev,
                                        rag_config: {
                                          ...prev.rag_config,
                                          profile_extraction: {
                                            ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                            terminology_mapping: newMapping
                                          }
                                        }
                                      }));
                                    }}
                                    className={`p-1.5 rounded transition-colors ${regexConfig.enabled ? 'bg-[#F4CC04]/20 text-[#F4CC04]' : 'text-[#888888] hover:text-white hover:bg-[#333333]'}`}
                                    title={regexConfig.enabled ? 'Desabilitar Regex' : 'Habilitar Regex'}
                                  >
                                    <Settings2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newMapping = { ...formData.rag_config?.profile_extraction?.terminology_mapping };
                                      delete newMapping[key];
                                      setFormData(prev => ({
                                        ...prev,
                                        rag_config: {
                                          ...prev.rag_config,
                                          profile_extraction: {
                                            ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                            terminology_mapping: newMapping
                                          }
                                        }
                                      }));
                                    }}
                                    className="p-1.5 text-[#888888] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Regex Config Panel */}
                            {isEditing && regexConfig.enabled && (
                              <div className="mb-3 p-3 bg-[#0a0a0a] rounded-lg border border-[#333333] space-y-3">
                                <h5 className="text-xs font-medium text-[#F4CC04]">Configuração Regex</h5>
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-xs text-[#888888] mb-1">Include Pattern</label>
                                    <input
                                      type="text"
                                      placeholder="ex: .* ou IRPJ|COFINS"
                                      value={regexConfig.include_pattern || ''}
                                      onChange={(e) => {
                                        const newRegexConfig = { ...regexConfig, include_pattern: e.target.value };
                                        const newParamConfig = { values, regex_config: newRegexConfig };
                                        const newMapping = {
                                          ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                          [key]: newParamConfig
                                        };
                                        setFormData(prev => ({
                                          ...prev,
                                          rag_config: {
                                            ...prev.rag_config,
                                            profile_extraction: {
                                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                              terminology_mapping: newMapping
                                            }
                                          }
                                        }));
                                      }}
                                      className="w-full px-2 py-1.5 bg-[#1a1a1a] border border-[#333333] text-white text-xs rounded focus:outline-none focus:border-[#F4CC04]"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-[#888888] mb-1">Exclude Pattern</label>
                                    <input
                                      type="text"
                                      placeholder="ex: test|exemplo"
                                      value={regexConfig.exclude_pattern || ''}
                                      onChange={(e) => {
                                        const newRegexConfig = { ...regexConfig, exclude_pattern: e.target.value || null };
                                        const newParamConfig = { values, regex_config: newRegexConfig };
                                        const newMapping = {
                                          ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                          [key]: newParamConfig
                                        };
                                        setFormData(prev => ({
                                          ...prev,
                                          rag_config: {
                                            ...prev.rag_config,
                                            profile_extraction: {
                                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                              terminology_mapping: newMapping
                                            }
                                          }
                                        }));
                                      }}
                                      className="w-full px-2 py-1.5 bg-[#1a1a1a] border border-[#333333] text-white text-xs rounded focus:outline-none focus:border-[#F4CC04]"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-[#888888] mb-1">Max Values</label>
                                    <input
                                      type="number"
                                      min="-1"
                                      value={regexConfig.max_values ?? -1}
                                      onChange={(e) => {
                                        const newRegexConfig = { ...regexConfig, max_values: parseInt(e.target.value) || -1 };
                                        const newParamConfig = { values, regex_config: newRegexConfig };
                                        const newMapping = {
                                          ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                          [key]: newParamConfig
                                        };
                                        setFormData(prev => ({
                                          ...prev,
                                          rag_config: {
                                            ...prev.rag_config,
                                            profile_extraction: {
                                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                              terminology_mapping: newMapping
                                            }
                                          }
                                        }));
                                      }}
                                      className="w-full px-2 py-1.5 bg-[#1a1a1a] border border-[#333333] text-white text-xs rounded focus:outline-none focus:border-[#F4CC04]"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={regexConfig.case_insensitive ?? true}
                                      onChange={(e) => {
                                        const newRegexConfig = { ...regexConfig, case_insensitive: e.target.checked };
                                        const newParamConfig = { values, regex_config: newRegexConfig };
                                        const newMapping = {
                                          ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                          [key]: newParamConfig
                                        };
                                        setFormData(prev => ({
                                          ...prev,
                                          rag_config: {
                                            ...prev.rag_config,
                                            profile_extraction: {
                                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                              terminology_mapping: newMapping
                                            }
                                          }
                                        }));
                                      }}
                                      className="w-4 h-4 rounded border-[#333333] bg-[#1a1a1a] checked:bg-[#F4CC04]"
                                    />
                                    <label className="text-xs text-[#888888]">Case Insensitive</label>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => testRegexForParameter(key)}
                                  disabled={isTestingRegex[key] || !regexConfig.include_pattern}
                                  className="flex items-center gap-2 px-3 py-1.5 bg-[#333333] hover:bg-[#444444] text-white text-xs rounded transition-colors disabled:opacity-50"
                                >
                                  <FlaskConical className="w-3.5 h-3.5" />
                                  {isTestingRegex[key] ? 'Testando...' : 'Testar Regex'}
                                </button>

                                {regexTestResults?.[key] && (
                                  <div className="mt-2 p-2 bg-[#1a1a1a] rounded border border-green-500/30">
                                    <p className="text-xs text-green-400 mb-1">
                                      Encontrados {regexTestResults[key].statistics[key]} valores
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {regexTestResults[key].terminology_mapping[key]?.slice(0, 10)?.map((value: string, idx: number) => (
                                        <span key={idx} className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                                          {value}
                                        </span>
                                      ))}
                                      {regexTestResults[key].terminology_mapping[key]?.length > 10 && (
                                        <span className="text-xs text-[#888888]">
                                          +{regexTestResults[key].terminology_mapping[key].length - 10} mais
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Values Tags */}
                            <div className="flex flex-wrap gap-1.5">
                              {Array.isArray(values) && values.map((val: string, idx: number) => (
                                <span key={idx} className="flex items-center gap-1 px-2 py-1 bg-[#333333] text-white text-xs rounded-full">
                                  {val}
                                  {isEditing && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const newValues = values.filter((_: string, i: number) => i !== idx);
                                        const newParamConfig = { values: newValues, regex_config: regexConfig };
                                        const newMapping = {
                                          ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                          [key]: newParamConfig
                                        };
                                        setFormData(prev => ({
                                          ...prev,
                                          rag_config: {
                                            ...prev.rag_config,
                                            profile_extraction: {
                                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                              terminology_mapping: newMapping
                                            }
                                          }
                                        }));
                                      }}
                                      className="ml-1 text-[#888888] hover:text-red-400"
                                    >
                                      ×
                                    </button>
                                  )}
                                </span>
                              ))}
                            </div>

                            {/* Add Value Input */}
                            {isEditing && (
                              <div className="flex gap-2 mt-2">
                                <input
                                  type="text"
                                  id={`new-term-${key}`}
                                  placeholder="Adicionar valor"
                                  className="flex-1 px-2 py-1.5 bg-[#0a0a0a] border border-[#333333] text-white text-xs rounded focus:outline-none focus:border-[#F4CC04]"
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                      e.preventDefault();
                                      const input = document.getElementById(`new-term-${key}`) as HTMLInputElement;
                                      const value = input.value.trim();
                                      if (value) {
                                        const newValues = [...values, value];
                                        const newParamConfig = { values: newValues, regex_config: regexConfig };
                                        const newMapping = {
                                          ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                          [key]: newParamConfig
                                        };
                                        setFormData(prev => ({
                                          ...prev,
                                          rag_config: {
                                            ...prev.rag_config,
                                            profile_extraction: {
                                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                              terminology_mapping: newMapping
                                            }
                                          }
                                        }));
                                        input.value = '';
                                      }
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const input = document.getElementById(`new-term-${key}`) as HTMLInputElement;
                                    const value = input.value.trim();
                                    if (value) {
                                      const newValues = [...values, value];
                                      const newParamConfig = { values: newValues, regex_config: regexConfig };
                                      const newMapping = {
                                        ...formData.rag_config?.profile_extraction?.terminology_mapping,
                                        [key]: newParamConfig
                                      };
                                      setFormData(prev => ({
                                        ...prev,
                                        rag_config: {
                                          ...prev.rag_config,
                                          profile_extraction: {
                                            ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                            terminology_mapping: newMapping
                                          }
                                        }
                                      }));
                                      input.value = '';
                                    }
                                  }}
                                  className="px-3 py-1.5 bg-[#333333] hover:bg-[#444444] text-white text-xs rounded transition-colors"
                                >
                                  Add
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Add New Parameter */}
                      {isEditing && (
                        <div className="flex gap-2 mt-2">
                          <input
                            type="text"
                            id="new-param-key"
                            placeholder="Novo parâmetro"
                            className="flex-1 px-3 py-2 bg-[#1a1a1a] border border-[#333333] text-white text-sm rounded focus:outline-none focus:border-[#F4CC04]"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.getElementById('new-param-key') as HTMLInputElement;
                              const key = input.value.trim();
                              if (key) {
                                const newMapping = {
                                  ...(formData.rag_config?.profile_extraction?.terminology_mapping || {}),
                                  [key]: {
                                    values: [],
                                    regex_config: {
                                      enabled: false,
                                      include_pattern: null,
                                      exclude_pattern: null,
                                      case_insensitive: true,
                                      max_values: -1
                                    }
                                  }
                                };
                                setFormData(prev => ({
                                  ...prev,
                                  rag_config: {
                                    ...prev.rag_config,
                                    profile_extraction: {
                                      ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                                      terminology_mapping: newMapping
                                    }
                                  }
                                }));
                                input.value = '';
                              }
                            }}
                            className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm rounded transition-colors"
                          >
                            Adicionar
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-[#888888] mt-2">
                      Defina parâmetros e seus valores permitidos que o sistema deve extrair das mensagens do usuário.
                    </p>
                  </div>
                </div>
              </div>

              {/* Document Search */}
              <div className="p-4 bg-[#1a1a1a] rounded-xl border border-[#333333]">
                <h4 className="text-white font-medium mb-4 pb-2 border-b border-[#333333]">
                  2. Busca de Documentos
                </h4>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">
                      Coluna de Conteúdo Principal
                    </label>
                    <input
                      type="text"
                      value={formData.rag_config?.document_search?.main_content_column || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          document_search: {
                            ...(prev.rag_config?.document_search || { base_sql_query: '', main_content_column: '', max_documents: 5 }),
                            main_content_column: e.target.value
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      className={inputBaseClass}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-[#cccccc]">Query SQL Base</label>
                      <button
                        type="button"
                        onClick={() => handleCopy(formData.rag_config?.document_search?.base_sql_query || '')}
                        className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded transition-colors"
                        title="Copiar"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <textarea
                      value={formData.rag_config?.document_search?.base_sql_query || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          document_search: {
                            ...(prev.rag_config?.document_search || { base_sql_query: '', main_content_column: '', max_documents: 5 }),
                            base_sql_query: e.target.value
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      rows={10}
                      className={`${inputBaseClass} font-mono text-sm resize-y min-h-[200px]`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">
                      Máximo de Documentos
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={formData.rag_config?.document_search?.max_documents || 5}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          document_search: {
                            ...(prev.rag_config?.document_search || { base_sql_query: '', main_content_column: '', max_documents: 5 }),
                            max_documents: parseInt(e.target.value)
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      className={inputBaseClass}
                    />
                  </div>
                </div>
              </div>

              {/* Document Filter */}
              <div className="p-4 bg-[#1a1a1a] rounded-xl border border-[#333333]">
                <h4 className="text-white font-medium mb-4 pb-2 border-b border-[#333333]">
                  3. Filtro de Documentos
                </h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-[#cccccc]">System Prompt</label>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setExpandedPrompt({
                            type: 'filter',
                            value: formData.rag_config?.document_filter?.system_prompt || '',
                            editable: isEditing
                          })}
                          className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded transition-colors"
                          title="Expandir"
                        >
                          <Maximize2 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopy(formData.rag_config?.document_filter?.system_prompt || '')}
                          className="p-1.5 text-[#888888] hover:text-white hover:bg-[#333333] rounded transition-colors"
                          title="Copiar"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <textarea
                      value={formData.rag_config?.document_filter?.system_prompt || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          document_filter: {
                            ...(prev.rag_config?.document_filter || { system_prompt: '', model_name: '', max_filtered_documents: 5 }),
                            system_prompt: e.target.value
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      rows={8}
                      className={`${inputBaseClass} font-mono text-sm resize-y min-h-[180px]`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">Modelo</label>
                    <select
                      value={formData.rag_config?.document_filter?.model_name || 'claude-4-5-haiku'}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          document_filter: {
                            ...(prev.rag_config?.document_filter || { system_prompt: '', model_name: '', max_filtered_documents: 5 }),
                            model_name: e.target.value
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      className={`${inputBaseClass} cursor-pointer`}
                    >
                      {getModelSelectOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}{option.isNew && ' 🆕'}{option.isRecommended && ' ⭐'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#cccccc] mb-2">
                      Máximo de Documentos Filtrados
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={formData.rag_config?.document_filter?.max_filtered_documents || 5}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        rag_config: {
                          ...prev.rag_config,
                          document_filter: {
                            ...(prev.rag_config?.document_filter || { system_prompt: '', model_name: '', max_filtered_documents: 5 }),
                            max_filtered_documents: parseInt(e.target.value)
                          }
                        }
                      }))}
                      disabled={!isEditing}
                      className={inputBaseClass}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          {isEditing && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-[#333333]">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    name: dataSource?.name || '',
                    description: dataSource?.description || '',
                    rag_type: dataSource?.rag_type || '',
                    rag_config: dataSource?.rag_config || {},
                    visibility_level: dataSource?.visibility_level || 'workspace',
                    created_by_group_id: dataSource?.created_by_group_id || null,
                    workspace_ids: dataSource?.visible_workspace_ids || [],
                    group_ids: dataSource?.visible_group_ids || []
                  });
                }}
                disabled={isLoading}
                className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black text-sm font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-w-[140px]"
              >
                {isLoading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : 'Salvar Alterações'}
              </button>
            </div>
          )}
        </form>
      </AdminCard>

      {/* Associated Agents Card */}
      <AdminCard
        title="Agentes Associados"
        headerAction={
          <button
            onClick={() => setShowAgentModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black text-sm font-medium rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Associar Agente
          </button>
        }
      >
        {dataSource.agents && dataSource.agents.length > 0 ? (
          <div className="space-y-2">
            {dataSource.agents.map((agent: Agent) => (
              <div
                key={agent.id}
                className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg border border-[#333333]"
              >
                <div
                  className="flex items-center gap-3 cursor-pointer flex-1"
                  onClick={() => navigate(`/admin/agents/${agent.id}`)}
                >
                  <div className="p-2 bg-[#333333] rounded-lg">
                    <Bot className="w-4 h-4 text-[#F4CC04]" />
                  </div>
                  <div>
                    <p className="text-white font-medium text-sm">{agent.name}</p>
                    {agent.description && (
                      <p className="text-[#888888] text-xs mt-0.5 line-clamp-1">{agent.description}</p>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDeassociateAgent(agent.id)}
                  className="px-3 py-1.5 text-red-400 text-xs hover:bg-red-500/10 rounded transition-colors"
                >
                  Remover
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[#888888] text-sm">Nenhum agente associado a este data source.</p>
        )}
      </AdminCard>

      {/* Danger Zone */}
      {!isEditing && (
        <div className="p-6 bg-red-500/5 border border-red-500/20 rounded-xl">
          <h3 className="text-red-400 font-semibold text-sm mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Zona de Perigo
          </h3>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium">Excluir Data Source</p>
              <p className="text-[#888888] text-xs mt-1">
                Esta ação não pode ser desfeita.
              </p>
            </div>
            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Excluir
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {isLoading ? 'Excluindo...' : 'Confirmar'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Agent Modal */}
      {showAgentModal && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setShowAgentModal(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-lg bg-[#2a2a2a] rounded-2xl z-50 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-[#333333]">
              <h2 className="text-lg font-semibold text-white">Associar Agente</h2>
              <button
                onClick={() => setShowAgentModal(false)}
                className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-[#333333]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Buscar agentes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2.5 pl-10 bg-[#1a1a1a] border border-[#333333] text-white placeholder-[#888888] text-sm rounded-lg focus:outline-none focus:border-[#F4CC04]"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888888]" />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingAgents ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="medium" />
                </div>
              ) : filteredAgents.length > 0 ? (
                <div className="space-y-2">
                  {filteredAgents.map(agent => {
                    const isAssociated = dataSource.agents?.some((a: Agent) => a.id === agent.id);
                    return (
                      <div key={agent.id} className="flex items-center justify-between p-3 bg-[#1a1a1a] rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-white font-medium text-sm">{agent.name}</p>
                          {agent.description && (
                            <p className="text-[#888888] text-xs mt-0.5 line-clamp-1">{agent.description}</p>
                          )}
                        </div>
                        <button
                          onClick={() => !isAssociated && handleAssociateAgent(agent.id)}
                          disabled={isAssociated}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                            isAssociated
                              ? 'bg-[#333333] text-[#888888] cursor-not-allowed'
                              : 'bg-[#F4CC04] hover:bg-[#D4B104] text-black'
                          }`}
                        >
                          {isAssociated ? 'Associado' : 'Associar'}
                        </button>
                      </div>
                    );
                  })}

                  {paginationData?.has_more && (
                    <div className="flex justify-center pt-4">
                      {isLoadingMore ? (
                        <LoadingSpinner size="small" />
                      ) : (
                        <button
                          onClick={handleLoadMore}
                          className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm rounded-lg transition-colors"
                        >
                          Carregar Mais
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-center text-[#888888] py-8">
                  {debouncedSearchQuery ? 'Nenhum agente encontrado.' : 'Nenhum agente disponível.'}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Expanded Prompt Modal */}
      {expandedPrompt && (
        <>
          <div className="fixed inset-0 bg-black/60 z-40" onClick={() => setExpandedPrompt(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-3xl bg-[#2a2a2a] rounded-2xl z-50">
            <div className="flex items-center justify-between p-6 border-b border-[#333333]">
              <h3 className="text-lg font-semibold text-white">
                {expandedPrompt.type === 'profile' ? 'Profile Extraction System Prompt' : 'Document Filter System Prompt'}
              </h3>
              <button
                onClick={() => setExpandedPrompt(null)}
                className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea
                value={expandedPrompt.value}
                onChange={expandedPrompt.editable ? (e) => setExpandedPrompt(prev => prev ? { ...prev, value: e.target.value } : null) : undefined}
                readOnly={!expandedPrompt.editable}
                rows={18}
                className={`${inputBaseClass} font-mono text-sm resize-y min-h-[350px]`}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => handleCopy(expandedPrompt.value)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copiar
                </button>
                {expandedPrompt.editable && (
                  <button
                    type="button"
                    onClick={() => {
                      if (expandedPrompt.type === 'profile') {
                        setFormData(prev => ({
                          ...prev,
                          rag_config: {
                            ...prev.rag_config,
                            profile_extraction: {
                              ...(prev.rag_config?.profile_extraction || { model_name: '', system_prompt: '', terminology_mapping: {} }),
                              system_prompt: expandedPrompt.value
                            }
                          }
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          rag_config: {
                            ...prev.rag_config,
                            document_filter: {
                              ...(prev.rag_config?.document_filter || { system_prompt: '', model_name: '', max_filtered_documents: 5 }),
                              system_prompt: expandedPrompt.value
                            }
                          }
                        }));
                      }
                      setExpandedPrompt(null);
                    }}
                    className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black text-sm font-medium rounded-lg transition-colors"
                  >
                    Salvar
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setExpandedPrompt(null)}
                  className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white text-sm rounded-lg transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DataSourceSettings;
